import api from "@/services/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  CreateTeamFormData,
  UpdateTeamFormData,
} from "@/schemas/teamSchemas";

interface GetTeamsParams {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
}

export const useGetTeams = (params?: GetTeamsParams) => {
  return useQuery({
    queryKey: ["getTeams", params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (params?.search) {
        searchParams.append("search", params.search);
      }

      if (params?.status && params.status !== "all") {
        searchParams.append("status", params.status);
      }

      if (params?.page) {
        searchParams.append("page", params.page.toString());
      }

      if (params?.limit) {
        searchParams.append("limit", params.limit.toString());
      }

      const queryString = searchParams.toString();
      const url = queryString ? `/field-teams?${queryString}` : "/field-teams";

      const response = await api.get(url);
      return response.data;
    },
  });
};

export const useGetTeamById = (id: string) => {
  return useQuery({
    queryKey: ["getTeam", id],
    queryFn: async () => {
      const response = await api.get(`/field-teams/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTeamFormData) => {
      const response = await api.post("/field-teams", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTeams"] });
    },
  });
};

export const useUpdateTeam = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateTeamFormData) => {
      const response = await api.put(`/field-teams/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTeams"] });
      queryClient.invalidateQueries({ queryKey: ["getTeam", id] });
    },
  });
};

export const useDeleteTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/field-teams/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getTeams"] });
    },
  });
};
